import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Badge,
  Divider,
  Tooltip,
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Timeline as ProgressIcon,
  AccountBalanceWallet as WalletIcon,
  Person as ProfileIcon,
  Notifications as NotificationsIcon,
  ExitToApp as LogoutIcon,
  Receipt as TransactionIcon,
  Help as SupportIcon,
} from '@mui/icons-material';
import AllamSchoolLogo from '../assets/white-logo.png'; // Adjust the path to your logo
import { LoadingContext } from '../context/LoadingContext';
import LoadingScreen from '../components/LoadingScreen';
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import LanguageSwitcher from '../components/LanguageSwitcher'; // Import LanguageSwitcher
import { useAuth } from '../auth/context/AuthContext';
import boopSound from '../sounds/boop.mp3';
import backgroundImage from '../assets/background.png'; // Add your background image path here
import { getWalletBalance } from '../services/api';

// Custom theme to override Tooltip styles
const theme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#000000', // Black background
          color: '#ffffff', // White text
          fontSize: '0.875rem', // Adjust font size if needed
        },
      },
    },
  },
});

const drawerWidth = 240;
const collapsedDrawerWidth = 80; // Increased width for the collapsed sidebar

const StudentLayout = () => {
  const { setLoading } = useContext(LoadingContext);
  const { t } = useTranslation(); // Hook to access translations
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true); // State to manage collapsed menu
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth(); // Get the logout function from AuthContext

  // Profile dropdown menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  // Example wallet balance in EGP
  const notificationCount = 3; // Replace with dynamic data if needed
  const { loading } = useContext(LoadingContext); // Access the loading state
  const audioRef = useRef(null);
  const [walletBalance, setWalletBalance] = useState(null);

  // Play sound effect
  const playSound = () => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    playSound(); // Play sound effect
  };

  // Handle profile menu open
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    playSound(); // Play sound effect
  };

  // Handle profile menu close
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
    playSound(); // Play sound effect
  };

  // Handle logout
  const handleLogout = () => {
    // Perform logout logic here (e.g., clear session, redirect to login page)
    logout(); // Call the logout function from AuthContext
    playSound(); // Play sound effect
    navigate('/login'); // Redirect to login page
    handleProfileMenuClose(); // Close the menu

  };

  // Handle profile view
  const handleProfileView = () => {
    playSound(); // Play sound effect
    navigate('/student/profile'); // Redirect to profile page
    handleProfileMenuClose(); // Close the menu
  };

  // Handle wallet recharge click
  const handleWalletRecharge = () => {
    playSound(); // Play sound effect
    navigate('/student/wallet'); // Redirect to wallet recharge page
  };

  const menuItems = useMemo(
    () => [
      {
        text: t('Home'), // Translated text
        icon: <DashboardIcon />,
        path: '/student/dashboard',
      },
      {
        text: t('Progress'), // Translated text
        icon: <ProgressIcon />,
        path: '/student/progress',
      },
      {
        text: t('Wallet'), // Translated text
        icon: <WalletIcon />,
        path: '/student/wallet',
      },
      {
        text: t('Transactions'), // Translated text
        icon: <TransactionIcon />,
        path: '/student/transactions',
      },{
        text: t('Support'), // Translated text
        icon: <SupportIcon />,
        path: '/student/',
      },
    ],
    [t]
  );

  const drawer = (
    <Box
      sx={{
        overflow: 'auto',
        width: isMobile ? drawerWidth : isCollapsed ? collapsedDrawerWidth : drawerWidth,
        transition: 'width 0.3s ease',
        backgroundColor: isCollapsed && !isMobile ? '#FFFFFF' : '#004aad', // White background for collapsed sidebar
        // Hide scrollbar but allow scrolling
        scrollbarWidth: 'none', // Firefox
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari, Edge
        },
      }}
    >
      <audio ref={audioRef}>
        <source src={boopSound} type="audio/mp3" />
        Your browser does not support the audio element.
    </audio>
      <Toolbar sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        {/* Allam School Logo */}
        <img
          src={AllamSchoolLogo}
          alt="Allam School Logo"
          style={{
            width: isCollapsed && !isMobile ? '80px' : '80px', // Smaller logo size
            height: 'auto',
            transition: 'width 0.3s ease',
          }}
        />
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <Tooltip
            key={item.text}
            title={item.text}
            placement="right"
            disableHoverListener={!isCollapsed || isMobile} // Disable tooltip when expanded or on mobile
          >
            <ListItem
              button
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: isCollapsed && !isMobile ? '#f5f5f5' : '#003a8c', // Highlight selected item
                  color: isCollapsed && !isMobile ? '#004aad' : '#FFFFFF', // Blue text for selected item in collapsed mode
                },
                '&.Mui-selected:hover': {
                  backgroundColor: isCollapsed && !isMobile ? '#f5f5f5' : '#003a8c',
                },
                justifyContent: isMobile ? 'flex-start' : isCollapsed ? 'center' : 'flex-start', // Center icons when collapsed (desktop only)
                color: isCollapsed && !isMobile ? '#004aad' : '#FFFFFF', // Blue text for collapsed mode
                mb: 1, // Add spacing between menu items
                borderRadius: 0, // Rectangular shape
                '&:hover': {
                  backgroundColor: isCollapsed && !isMobile ? '#f5f5f5' : '#003a8c', // Light gray for collapsed, darker blue for expanded
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 'auto', color: isCollapsed && !isMobile ? '#004aad' : '#FFFFFF' }}> {/* Blue icons */}
                {React.cloneElement(item.icon, { fontSize: 'small' })} {/* Smaller icons */}
              </ListItemIcon>
              {!isCollapsed || isMobile ? (
                <ListItemText
                  primary={item.text}
                  sx={{ color: isCollapsed && !isMobile ? '#004aad' : '#FFFFFF' }} // Blue text for collapsed mode
                />
              ) : null}
            </ListItem>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

    // Fetch wallet balance from API
    useEffect(() => {
      const fetchWalletBalance = async () => {
        setLoading(true);
        try {
          const response = await getWalletBalance();
          setWalletBalance(response.balance);
        } catch (error) {
          console.error('Error fetching wallet balance:', error);
          //setSnackbar({ open: true, message: 'Failed to fetch wallet balance. Please try again later.', severity: 'error' });
        } finally {
          setLoading(false);
        }
      };
  
      fetchWalletBalance();
    }, [setLoading]);
  return (
    <ThemeProvider theme={theme}>
      {loading && <LoadingScreen />} {/* Show loading screen when loading is true */}
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        {/* AppBar */}
        <AppBar
          position="fixed"
          color="default"
          sx={{
            width: { sm: `calc(100% - ${isCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
            ml: { sm: `${isCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
            transition: 'width 0.3s ease, margin 0.3s ease',
            backgroundColor: '#FFFFFF', // White background for the app bar
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)', // Subtle shadow for depth
          }}
        >
          <Toolbar sx={{ minHeight: '48px' }}> {/* Thinner toolbar */}
            {/* Menu Icon for Mobile */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' }, color: '#004aad' }} // Blue color for the menu icon
            >
              <MenuIcon fontSize="small" /> {/* Smaller menu icon */}
            </IconButton>

            {/* Spacer to push icons to the right */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Notification Icon with Tooltip */}
            <Tooltip title={t('viewNotifications')} placement="bottom"> {/* Translated tooltip */}
              <IconButton color="inherit" sx={{ mr: 2, color: '#004aad' }}> {/* Blue color for the icon */}
                <Badge badgeContent={notificationCount} color="error">
                  <NotificationsIcon fontSize="small" /> {/* Smaller notification icon */}
                </Badge>
              </IconButton>
            </Tooltip>



            {/* Wallet Icon with Tooltip */}
            <Tooltip title={t('rechargeWallet')} placement="bottom"> {/* Translated tooltip */}
              <IconButton
                component={Link}
                to="/student/wallet"
                onClick={handleWalletRecharge}
                color="inherit"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#004aad' }} // Blue color for the icon
              >
                <WalletIcon fontSize="small" /> {/* Smaller wallet icon */}
                <Typography variant="body2" sx={{ fontFamily: 'Fredoka One', color: '#004aad' }}> {/* Blue text */}
                  {walletBalance} EGP
                </Typography>
              </IconButton>
            </Tooltip>

            {/* Divider between Notification and Wallet Icons */}
            <Divider orientation="vertical" flexItem sx={{ml:1, borderColor: theme.palette.divider }} />

            {/* Profile Picture with Dropdown Menu */}
            <IconButton
              edge="end"
              aria-label="profile"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              sx={{ ml: 2 }}
            >
              <Avatar
                src="https://via.placeholder.com/150" // Replace with the actual profile picture URL
                alt="Profile"
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>

            {/* Profile Dropdown Menu */}
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleProfileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleProfileView}>
                <ListItemIcon>
                  <ProfileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('viewProfile')} />
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('logout')} />
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Sidebar Drawer */}
        <Box
          component="nav"
          sx={{
            width: { sm: isCollapsed ? collapsedDrawerWidth : drawerWidth },
            flexShrink: { sm: 0 },
            transition: 'width 0.3s ease',
          }}
        >
          <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: isMobile ? drawerWidth : isCollapsed ? collapsedDrawerWidth : drawerWidth,
                boxSizing: 'border-box',
                transition: 'width 0.3s ease',
                backgroundColor: isCollapsed && !isMobile ? '#FFFFFF' : '#004aad', // White background for collapsed sidebar
                // Hide scrollbar but allow scrolling
                scrollbarWidth: 'none', // Firefox
                '&::-webkit-scrollbar': {
                  display: 'none', // Chrome, Safari, Edge
                },
              },
            }}
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              width: { sm: `calc(100% - ${isCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
              transition: 'width 0.3s ease',
              // backgroundImage: `url(${backgroundImage})`, // Add your background image path here
              // backgroundSize: 'cover', // Cover the entire box
              // backgroundPosition: 'center', // Center the image
              // backgroundRepeat: 'no-repeat', // No repeating
              // // Hide scrollbar but allow scrolling
              scrollbarWidth: 'none', // Firefox
              '&::-webkit-scrollbar': {
                display: 'none', // Chrome, Safari, Edge
              },
            }}
          >
            <Toolbar sx={{ minHeight: '48px' }} /> {/* Thinner spacer for AppBar */}
          <Outlet /> {/* Renders the nested routes */}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default StudentLayout;